import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getDriverStats,
  getDrivers,
  getDriverById,
  updateDriverStatus,
  updateDriver,
  deleteDriver,
} from '../services/api';

export const fetchDriverStats = createAsyncThunk(
  'driver/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getDriverStats();
      return data?.data || data || { total: 0, pending: 0, approved: 0, rejected: 0 };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch driver stats'
      );
    }
  }
);

export const fetchDrivers = createAsyncThunk(
  'driver/fetchDrivers',
  async (params, { rejectWithValue }) => {
    try {
      const data = await getDrivers(params);
      return Array.isArray(data) ? data : data?.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch drivers'
      );
    }
  }
);

export const fetchDriverById = createAsyncThunk(
  'driver/fetchDriverById',
  async (id, { rejectWithValue }) => {
    try {
      const data = await getDriverById(id);
      return data?.data || data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch driver'
      );
    }
  }
);

export const updateDriverStatusAsync = createAsyncThunk(
  'driver/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await updateDriverStatus(id, status);
      return { id, status, data: response?.data || response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update driver status'
      );
    }
  }
);

export const updateDriverAsync = createAsyncThunk(
  'driver/updateDriver',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await updateDriver(id, formData);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update driver'
      );
    }
  }
);

export const deleteDriverAsync = createAsyncThunk(
  'driver/deleteDriver',
  async (id, { rejectWithValue }) => {
    try {
      await deleteDriver(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to delete driver'
      );
    }
  }
);

const driverSlice = createSlice({
  name: 'driver',
  initialState: {
    drivers: [],
    stats: { total: 0, pending: 0, approved: 0, rejected: 0 },
    selectedDriver: null,
    status: 'idle',
    statsStatus: 'idle',
    error: null,
    actionLoading: false,
    actionError: null,
  },
  reducers: {
    clearDriverError: (state) => {
      state.error = null;
      state.actionError = null;
    },
    setSelectedDriver: (state, action) => {
      state.selectedDriver = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch stats
      .addCase(fetchDriverStats.pending, (state) => {
        state.statsStatus = 'loading';
      })
      .addCase(fetchDriverStats.fulfilled, (state, action) => {
        state.statsStatus = 'succeeded';
        state.stats = action.payload;
      })
      .addCase(fetchDriverStats.rejected, (state) => {
        state.statsStatus = 'failed';
      })
      // Fetch drivers
      .addCase(fetchDrivers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchDrivers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.drivers = action.payload;
      })
      .addCase(fetchDrivers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Fetch single driver
      .addCase(fetchDriverById.fulfilled, (state, action) => {
        state.selectedDriver = action.payload;
      })
      // Update status
      .addCase(updateDriverStatusAsync.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updateDriverStatusAsync.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { id, status } = action.payload;
        state.drivers = state.drivers.map((d) =>
          d.id === id ? { ...d, status } : d
        );
        if (state.selectedDriver && state.selectedDriver.id === id) {
          state.selectedDriver.status = status;
        }
      })
      .addCase(updateDriverStatusAsync.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })
      // Update driver full
      .addCase(updateDriverAsync.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updateDriverAsync.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        if (updated && updated.id) {
          state.drivers = state.drivers.map((d) =>
            d.id === updated.id ? { ...d, ...updated } : d
          );
          if (state.selectedDriver && state.selectedDriver.id === updated.id) {
            state.selectedDriver = { ...state.selectedDriver, ...updated };
          }
        }
      })
      .addCase(updateDriverAsync.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })
      // Delete driver
      .addCase(deleteDriverAsync.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(deleteDriverAsync.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.drivers = state.drivers.filter((d) => d.id !== action.payload);
        if (state.selectedDriver && state.selectedDriver.id === action.payload) {
          state.selectedDriver = null;
        }
      })
      .addCase(deleteDriverAsync.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });
  },
});

export const { clearDriverError, setSelectedDriver } = driverSlice.actions;
export default driverSlice.reducer;
